/**
 * =============================================================================
 * Componente: FormModalShell
 * Versão: 1.0.0
 * Release: 2026-05-02 — Shell padrão (premium) para modais de criação/edição
 *
 * Descrição:
 *   Wrapper de Dialog que padroniza o LEIAUTE dos modais de cadastro/edição
 *   do sistema, espelhando o visual já consagrado no TaskFormDialog
 *   (módulo Tarefas):
 *
 *     ┌──────────────────────────────────────────┐
 *     │ [icone] Título                         ✕ │  header (border-b, bg/80 blur)
 *     │         Descrição contextual             │
 *     ├──────────────────────────────────────────┤
 *     │ [Aba1] [Aba2] [Aba3] ...                 │  ModalTabsBar (opcional)
 *     ├──────────────────────────────────────────┤
 *     │                                          │
 *     │    ScrollArea com children (form)        │
 *     │                                          │
 *     ├──────────────────────────────────────────┤
 *     │              [Cancelar] [Submit ▷]       │  footer (border-t, bg-muted/20)
 *     └──────────────────────────────────────────┘
 *
 *   Uso típico:
 *     <FormModalShell
 *       open={open}
 *       onOpenChange={setOpen}
 *       icon={Megaphone}
 *       title="Editar campanha"
 *       description="Configure as regras..."
 *       tabs={CAMPAIGN_TABS}
 *       activeTab={tab}
 *       onTabChange={setTab}
 *       onSubmit={handleSubmit}
 *       isSubmitting={isPending}
 *       submitLabel="Salvar alterações"
 *     >
 *       {tab === 'general' && <FormModalSection ...>...</FormModalSection>}
 *     </FormModalShell>
 *
 *   IMPORTANTE: este componente padroniza apenas o ESQUELETO. O conteúdo
 *   interno (campos, validações, mutations) é responsabilidade do consumidor.
 * =============================================================================
 */
import type { LucideIcon } from 'lucide-react';
import { Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  ModalTabsBar,
  type ModalTabConfig,
} from '@/components/shared/ModalTabsBar';
import { cn } from '@/lib/utils';

/** Larguras pré-aprovadas para o DialogContent. */
type ShellMaxWidth = 'max-w-3xl' | 'max-w-4xl' | 'max-w-5xl' | 'max-w-6xl';

interface FormModalShellProps<TTabId extends string = string> {
  /** Estado de abertura do Dialog. */
  open: boolean;
  onOpenChange: (open: boolean) => void;

  /** Ícone exibido em "box" arredondada à esquerda do título. */
  icon: LucideIcon;
  /** Título principal do modal. */
  title: string;
  /** Linha de descrição secundária (opcional). */
  description?: string;

  /**
   * Configuração das abas. Quando informada (junto com activeTab/onTabChange),
   * o shell renderiza a barra de abas padrão (ModalTabsBar).
   */
  tabs?: ModalTabConfig<TTabId>[];
  activeTab?: TTabId;
  onTabChange?: (id: TTabId) => void;

  /** Conteúdo do form (já recebe padding e ScrollArea). */
  children: React.ReactNode;

  /** Label do botão primário (ex: "Salvar", "Criar campanha"). */
  submitLabel: string;
  /** Ícone opcional para o botão primário. */
  submitIcon?: LucideIcon;
  /** Label do botão de cancelar (default: "Cancelar"). */
  cancelLabel?: string;

  /** Handler do submit. */
  onSubmit: () => void;
  /** Estado "salvando" — desabilita botões e exibe spinner. */
  isSubmitting?: boolean;
  /** Desabilita o submit por validação externa (mantém clicável quando false). */
  isSubmitDisabled?: boolean;
  /** Permite ocultar a barra inferior (raro — útil para fluxos custom). */
  hideFooter?: boolean;

  /** Largura máxima do DialogContent (default: max-w-5xl). */
  maxWidth?: ShellMaxWidth;
  /**
   * Aplica z-[70] no DialogContent para empilhar corretamente quando este modal
   * é aberto sobre outro Dialog (ex.: criação de subitem).
   */
  zIndexBoost?: boolean;

  /** Padding interno do corpo. Default 'p-6 space-y-6'. */
  bodyClassName?: string;
}

/**
 * Shell padronizado para modais de criação/edição.
 * Replica o visual premium do TaskFormDialog para uso em outros módulos.
 */
export function FormModalShell<TTabId extends string = string>({
  open,
  onOpenChange,
  icon: Icon,
  title,
  description,
  tabs,
  activeTab,
  onTabChange,
  children,
  submitLabel,
  submitIcon: SubmitIcon,
  cancelLabel = 'Cancelar',
  onSubmit,
  isSubmitting = false,
  isSubmitDisabled = false,
  hideFooter = false,
  maxWidth = 'max-w-5xl',
  zIndexBoost = false,
  bodyClassName = 'p-6 space-y-6',
}: FormModalShellProps<TTabId>) {
  // Renderiza a barra de abas apenas se a configuração mínima estiver presente
  const showTabs =
    !!tabs && tabs.length > 0 && !!activeTab && !!onTabChange;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          maxWidth,
          'max-h-[90vh] flex flex-col p-0 gap-0',
          zIndexBoost && 'z-[70]',
        )}
      >
        {/* ───────────── Header premium ───────────── */}
        <DialogHeader className="flex flex-row items-center gap-3 px-6 py-4 border-b border-border/50 bg-background/80 backdrop-blur-sm">
          <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-primary/10 ring-1 ring-primary/20 shrink-0">
            <Icon className="w-5 h-5 text-primary" />
          </div>
          <div className="flex flex-col text-left min-w-0">
            <DialogTitle className="text-base font-semibold leading-tight truncate">
              {title}
            </DialogTitle>
            {description && (
              <DialogDescription className="text-xs text-muted-foreground mt-0.5 truncate">
                {description}
              </DialogDescription>
            )}
          </div>
        </DialogHeader>

        {/* ───────────── Barra de abas (opcional) ───────────── */}
        {showTabs && (
          <ModalTabsBar
            tabs={tabs!}
            activeTab={activeTab!}
            onTabChange={onTabChange!}
          />
        )}

        {/* ───────────── Corpo scrollável ───────────── */}
        <ScrollArea className="flex-1 overflow-auto">
          <div className={bodyClassName}>{children}</div>
        </ScrollArea>

        {/* ───────────── Footer premium ───────────── */}
        {!hideFooter && (
          <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-border/50 bg-muted/20 shrink-0">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
              className="text-muted-foreground hover:text-foreground"
            >
              {cancelLabel}
            </Button>
            <Button
              size="sm"
              onClick={onSubmit}
              disabled={isSubmitting || isSubmitDisabled}
              className="min-w-[140px] gap-2"
            >
              {isSubmitting && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              {!isSubmitting && SubmitIcon && (
                <SubmitIcon className="w-3.5 h-3.5" />
              )}
              {submitLabel}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
